import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectIsLoggedIn } from "../Redux/authSlice";
import { useGetMyInvitesQuery } from "../Redux/orgApi";
import JoinTeamDialog from "../component/TeamInvite/JoinTeamDialog";
import { useTeamAccess } from "../pages/Team/useTeamMembership";
import { useNotifications } from "./NotificationsContext";

const TeamInviteContext = createContext(null);

// Title of the in-app invite notification (notifyInvite in the backend's
// utils/teamNotify.js). Used to find the invite a notification is about.
const inviteNotificationTitle = (orgName) => `${orgName} invited you to team`;

// Invites the user chose to Ignore on the banner (this browser only). Keyed by
// invite and send time, so a resend shows the banner again. The invite stays
// in the notifications either way.
const IGNORED_KEY = "zt_team_invites_ignored";
const ignoreKeyOf = (invite) => `${invite.id}:${invite.sentAt || ""}`;
const readIgnored = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(IGNORED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const writeIgnored = (set) => {
  try {
    // The most recent ones are enough; old invites have expired anyway.
    localStorage.setItem(IGNORED_KEY, JSON.stringify([...set].slice(-50)));
  } catch {
    // Storage blocked: ignored for this visit only.
  }
};

/**
 * ZeroTyping Teams invites addressed to the signed-in user (GET
 * /api/org/invites/mine), shared by the notification list and the invite
 * banner, plus the "Join <team>?" dialog they both open.
 */
export const TeamInviteProvider = ({ children }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { data } = useGetMyInvitesQuery(undefined, { skip: !isLoggedIn });
  const { membership } = useTeamAccess();
  const { notifications, markAsRead } = useNotifications();
  // Invites answered in this session (Join or Decline). Hidden at once,
  // before the invite list refetches.
  const [answeredIds, setAnsweredIds] = useState(() => new Set());

  // Pending invites, minus any from the team the user is already in.
  const currentOrgId = membership?.orgId ?? null;
  const invites = useMemo(
    () =>
      isLoggedIn
        ? (data?.invites ?? []).filter((i) => i.orgId !== currentOrgId && !answeredIds.has(i.id))
        : [],
    [isLoggedIn, data, currentOrgId, answeredIds]
  );

  // Only the newest notification about each pending invite gets the View
  // Invite button; older ones from the same team (past invites) stay plain.
  const inviteByNotificationId = useMemo(() => {
    const byId = new Map();
    const newestFirst = notifications
      .filter((n) => n.type === "team.invite")
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    for (const invite of invites) {
      const latest = newestFirst.find((n) => n.title === inviteNotificationTitle(invite.orgName) && !byId.has(n.id));
      if (latest) byId.set(latest.id, invite);
    }
    return byId;
  }, [notifications, invites]);

  const [openInvite, setOpenInvite] = useState(null);

  // The pending invite a notification is about, or null when it isn't the
  // newest notification for an invite that is still pending.
  const inviteForNotification = useCallback(
    (notification) => {
      return inviteByNotificationId.get(notification?.id) || null;
    },
    [inviteByNotificationId]
  );

  // The banner shows the newest invite the user hasn't ignored.
  const [ignored, setIgnored] = useState(readIgnored);
  const bannerInvites = useMemo(() => invites.filter((i) => !ignored.has(ignoreKeyOf(i))), [invites, ignored]);
  const ignoreInvite = useCallback((invite) => {
    setIgnored((prev) => {
      const next = new Set(prev).add(ignoreKeyOf(invite));
      writeIgnored(next);
      return next;
    });
  }, []);

  const showInvite = useCallback((invite) => setOpenInvite(invite), []);
  const closeInvite = useCallback(() => setOpenInvite(null), []);

  // After Join or Decline: hide the invite and mark every notification about
  // it as read, so no View Invite button is left behind.
  const markAnswered = useCallback(
    (invite) => {
      setAnsweredIds((prev) => new Set(prev).add(invite.id));
      const title = inviteNotificationTitle(invite.orgName);
      notifications
        .filter((n) => n.type === "team.invite" && n.title === title && !n.read)
        .forEach((n) => markAsRead(n.id));
    },
    [notifications, markAsRead]
  );

  const value = useMemo(
    () => ({ invites, bannerInvites, ignoreInvite, inviteForNotification, showInvite }),
    [invites, bannerInvites, ignoreInvite, inviteForNotification, showInvite]
  );

  return (
    <TeamInviteContext.Provider value={value}>
      {children}
      {openInvite && <JoinTeamDialog invite={openInvite} onClose={closeInvite} onAnswered={markAnswered} />}
    </TeamInviteContext.Provider>
  );
};

export const useTeamInvites = () => {
  const ctx = useContext(TeamInviteContext);
  if (!ctx) {
    throw new Error("useTeamInvites must be used within a <TeamInviteProvider>");
  }
  return ctx;
};
